<script setup lang="ts">
import httpClient from '@/utils/httpClient'
import { useMagicKeys, whenever } from '@vueuse/core'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar'
import { Button } from './ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { useUserQuery } from '@/queries/user'
import { StaffSettingsRouteName } from '@/views/settings/routes'
import { useQueryClient } from '@tanstack/vue-query'
import { clear } from 'idb-keyval'
import { sentryUtils } from '@/lib/sentry'

const queryClient = useQueryClient()
const router = useRouter()
const open = ref(false)
const { data: user } = useUserQuery()
const keys = useMagicKeys({
    passive: false,
    onEventFired(e) {
        if (e.type === 'keydown' && (e.ctrlKey || e.metaKey) && (e.key === ',' || e.key === 'q')) {
            e.preventDefault()
        }
    },
})

whenever(keys.ctrl_comma, () => {
    goToSettings()
})

whenever(keys.meta_comma, () => {
    goToSettings()
})

whenever(keys.ctrl_q, () => {
    logout()
})

function goToSettings() {
    router.push({ name: StaffSettingsRouteName.Profile })
}

function logout() {
    httpClient.post('logout').then(async () => {
        // Clear Sentry user context on logout
        sentryUtils.setUser(null)

        queryClient.clear()
        await clear()

        await router.push({ name: 'login' })
    })
}
</script>

<template>
    <DropdownMenu v-model:open="open">
        <DropdownMenuTrigger as-child>
            <Button variant="ghost" class="relative h-8 w-8 rounded-full">
                <Avatar class="h-8 w-8">
                    <AvatarImage :src="user?.profile_photo_url" alt="@shadcn" />
                    <AvatarFallback>{{ user?.name_initial }}</AvatarFallback>
                </Avatar>
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="w-56" align="end">
            <DropdownMenuLabel class="font-normal flex">
                <div class="flex flex-col space-y-1">
                    <p class="text-sm font-medium leading-none">{{ user?.full_name }}</p>
                    <p class="text-xs leading-none text-muted-foreground">{{ user?.email }}</p>
                </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
                <DropdownMenuItem as-child>
                    <router-link
                        :to="{ name: StaffSettingsRouteName.Profile }"
                        class="flex items-center justify-between w-full"
                    >
                        <span>Settings</span>
                        <DropdownMenuShortcut>Ctrl + ,</DropdownMenuShortcut>
                    </router-link>
                </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem @click="logout">
                Log out
                <DropdownMenuShortcut>Ctrl + Q</DropdownMenuShortcut>
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
</template>
